/**
 * Preview Surface — S2-T3.1 + before/after toggle (S2-T3.2).
 *
 * Renders the stamped PDF with pdf.js: page 1 at full fidelity plus a
 * filmstrip of pages 2-5 as thumbnails, with a "+N more pages stamped"
 * indicator for longer documents. A Before/After toggle flips the preview
 * between the original upload and the stamped output so the user can see
 * exactly what the stamp added. The engine run lives in App; this component
 * only displays the bytes it's given.
 */

import { useEffect, useRef, useState } from "react";

import { getDocument, type PDFDocumentProxy } from "../pdf-viewer";
import styles from "./PreviewSurface.module.css";

const FILMSTRIP_LIMIT = 5; // page 1 + up to 4 thumbnails
const PAGE_ONE_SCALE = 1.5;
const THUMB_SCALE = 0.3;

type View = "before" | "after";

interface PreviewSurfaceProps {
  /** Stamped PDF bytes, or null when nothing is stamped yet. */
  stamped: Uint8Array | null;
  /** Original (unstamped) upload bytes, for the Before view. */
  original: Uint8Array | null;
  /** True while the engine is producing the (first) stamped output. */
  stamping?: boolean;
  /** True when the engine failed to process the file (encrypted/damaged). */
  errored?: boolean;
}

export function PreviewSurface({ stamped, original, stamping = false, errored = false }: PreviewSurfaceProps) {
  const [view, setView] = useState<View>("after");
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [status, setStatus] = useState<"empty" | "rendering" | "ready" | "error">("empty");

  const bytes = view === "before" ? (original ?? stamped) : (stamped ?? original);
  const canToggle = stamped !== null && original !== null;
  // While the first stamp is in flight, show a busy state instead of loading
  // anything into pdf.js (avoids a wasted render of the original).
  const showStamping = stamping && !stamped;
  // The engine couldn't stamp the file (encrypted / damaged / unreadable):
  // show a clear message rather than falling back to rendering the original.
  const showEngineError = errored && !stamped;

  useEffect(() => {
    if (showStamping || showEngineError) return;
    if (!bytes) {
      setDoc(null);
      setStatus("empty");
      return;
    }

    let cancelled = false;
    let loaded: PDFDocumentProxy | null = null;
    setStatus("rendering");

    // pdf.js consumes (transfers) the buffer it's given, so hand it a copy —
    // the caller keeps the original stamped bytes for the before/after toggle.
    getDocument({ data: bytes.slice() })
      .promise.then((d) => {
        if (cancelled) {
          void d.loadingTask.destroy();
          return;
        }
        loaded = d;
        setDoc(d);
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
      void loaded?.loadingTask.destroy();
    };
  }, [bytes, showStamping, showEngineError]);

  // Engine couldn't process the file (encrypted, password-protected, damaged).
  if (showEngineError) {
    return (
      <section className={styles.surface} aria-label="Preview">
        <div className={styles.error} role="alert">
          We couldn&rsquo;t process this PDF. It may be encrypted, password-protected, or damaged. Try
          another file.
        </div>
      </section>
    );
  }

  // First-render progress: the engine is producing the initial stamped output.
  if (showStamping) {
    return (
      <section className={styles.surface} aria-label="Preview">
        <div className={styles.status} role="status">
          Stamping your preview…
        </div>
      </section>
    );
  }

  if (status === "empty") {
    return (
      <section className={styles.surface} aria-label="Preview">
        <div className={styles.empty}>Drop a PDF to see it stamped here.</div>
      </section>
    );
  }

  if (status === "error") {
    return (
      <section className={styles.surface} aria-label="Preview">
        <div className={styles.error} role="alert">
          Couldn&rsquo;t render this PDF. It may be encrypted or damaged.
        </div>
      </section>
    );
  }

  const pageCount = doc?.numPages ?? 0;
  const thumbPages = doc ? rangeFrom(2, Math.min(FILMSTRIP_LIMIT, pageCount)) : [];
  const remaining = Math.max(0, pageCount - FILMSTRIP_LIMIT);

  return (
    <section className={styles.surface} aria-label="Preview">
      {canToggle && (
        <div className={styles.toggle} role="group" aria-label="Before or after">
          <button
            type="button"
            className={`${styles.toggleButton} ${view === "before" ? styles.toggleActive : ""}`}
            aria-pressed={view === "before"}
            onClick={() => setView("before")}
          >
            Before
          </button>
          <button
            type="button"
            className={`${styles.toggleButton} ${view === "after" ? styles.toggleActive : ""}`}
            aria-pressed={view === "after"}
            onClick={() => setView("after")}
          >
            After
          </button>
        </div>
      )}
      {status === "rendering" || !doc ? (
        <div className={styles.status}>Rendering preview…</div>
      ) : (
        <>
          <PdfPageCanvas doc={doc} pageNumber={1} scale={PAGE_ONE_SCALE} className={styles.pageOne} />
          {(thumbPages.length > 0 || remaining > 0) && (
            <div className={styles.filmstrip} aria-label="More pages">
              {thumbPages.map((n) => (
                <PdfPageCanvas key={n} doc={doc} pageNumber={n} scale={THUMB_SCALE} className={styles.thumb} />
              ))}
              {remaining > 0 && <div className={styles.more}>+{remaining} more pages stamped</div>}
            </div>
          )}
          {/* Brand watermark — preview only (there is no download). Lives inside
              the surface so it travels when the preview is screenshotted. */}
          <p className={styles.watermark}>
            Stamped with <strong>Imprint</strong> at imprint.starkly.cc
          </p>
        </>
      )}
    </section>
  );
}

interface PdfPageCanvasProps {
  doc: PDFDocumentProxy;
  pageNumber: number;
  scale: number;
  className?: string;
}

function PdfPageCanvas({ doc, pageNumber, scale, className }: PdfPageCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const page = await doc.getPage(pageNumber);
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (!canvas || !ctx || cancelled) return; // jsdom has no 2d context — no-op in tests
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      await page.render({ canvasContext: ctx, viewport, canvas }).promise;
    })();
    return () => {
      cancelled = true;
    };
  }, [doc, pageNumber, scale]);

  return <canvas ref={canvasRef} className={className} aria-label={`Page ${pageNumber}`} />;
}

function rangeFrom(start: number, end: number): number[] {
  const out: number[] = [];
  for (let i = start; i <= end; i += 1) out.push(i);
  return out;
}
